export const GENRES = [
  "fantasy",
  "romance",
  "thriller",
  "mystery",
  "science fiction",
] as const

export const STAGES = [
  {
    num: "I",
    name: "Foundation",
    copy: "Genre, tone, setting, heat. The components your book is made of, chosen deliberately.",
  },
  {
    num: "II",
    name: "Characters",
    copy: "Cast archetypes and narrative roles, drawn from the foundation you set.",
  },
  {
    num: "III",
    name: "Plot",
    copy: "Structure the arc — the promise, the midpoint, the second-act turn.",
  },
  {
    num: "IV",
    name: "Outline",
    copy: "Chapter-by-chapter scaffolding you can rearrange before a word is drafted.",
  },
  {
    num: "V",
    name: "Content",
    copy: "A complete draft, chapter by chapter. Yours to refine, yours to keep.",
  },
] as const

export const FEATURES = [
  {
    icon: "list-checks" as const,
    name: "Real-time trope analysis",
    copy: "See which tropes your premise leans on — and which readers will miss — as you choose.",
  },
  {
    icon: "pen-line" as const,
    name: "Structural edit",
    copy: "A developmental pass on every chapter: pacing, stakes, continuity.",
  },
  {
    icon: "book-open" as const,
    name: "Chapter & scene editor",
    copy: "Rework any scene without losing the thread of the outline around it.",
  },
  {
    icon: "feather" as const,
    name: "Style & grammar analysis",
    copy: "Line-level polish tuned to your voice, not a house style.",
  },
  {
    icon: "file-down" as const,
    name: "Publication-ready export",
    copy: "EPUB and print-ready files, formatted for the major storefronts.",
  },
  {
    icon: "users" as const,
    name: "Pen names & series",
    copy: "Every identity and every backlist, organized in one place.",
  },
] as const

export const OLD_WAY = [
  { item: "Market research", cost: "hours of scrolling" },
  { item: "Developmental editor", cost: "$1,500" },
  { item: "Formatting software", cost: "$250" },
  { item: "Writing software", cost: "$50" },
  { item: "Style & grammar tool", cost: "$120/year" },
] as const

export const NEW_WAY = [
  "Real-time trope analysis",
  "Structural edit",
  "Publication-ready export",
  "Chapter & scene editor",
  "Style & grammar analysis",
] as const

/**
 * Testimonials gate (PO 2026-08-20): hidden until REAL quotes from the
 * founding-author group replace the placeholder copy below. Flip to
 * true once QUOTES holds genuine, attributed quotes.
 */
export const SHOW_BETA_QUOTES = false

export const QUOTES = [
  {
    text: "I'd outlined the same trilogy three times and never gotten past chapter four. The Outline stage is where it finally held together.",
    initial: "M",
    name: "M. Calloway",
    role: "Epic fantasy · 2 manuscripts",
  },
  {
    text: "The trope analysis told me my romance had no third-act misunderstanding. It was right, and readers would have noticed.",
    initial: "J",
    name: "Juniper Hale",
    role: "Contemporary romance",
  },
  {
    text: "It doesn't write instead of you. It remembers everything you decided, so the draft sounds like the book you meant.",
    initial: "R",
    name: "R. Osei",
    role: "First-time author",
  },
] as const

/** Plan ladder — prices shown as Coming soon until public rates are set. */
export const PLANS = [
  {
    id: "hobbyist",
    name: "Hobbyist",
    price: "Coming soon",
    period: "",
    blurb: "For authors testing the pipeline on a first book.",
    features: [
      "All five stages, Foundation to Content",
      "AI usage drawn as you generate",
      "Pen names and series",
      "Standard support",
    ],
    highlighted: false,
    badge: null as string | null,
  },
  {
    id: "author",
    name: "Author",
    price: "Coming soon",
    period: "",
    blurb: "For working writers drafting regularly.",
    features: [
      "Everything in Hobbyist",
      "Higher monthly AI usage",
      "Priority support",
      "Analytics on your backlist",
    ],
    highlighted: true,
    badge: "Most popular",
  },
  {
    id: "pro",
    name: "Pro",
    price: "Coming soon",
    period: "",
    blurb: "For heavy generation and multi-book years.",
    features: [
      "Everything in Author",
      "High AI usage for full pipelines",
      "Priority support",
    ],
    highlighted: false,
    badge: null as string | null,
  },
  {
    id: "publisher",
    name: "Publisher",
    price: "Coming soon",
    period: "",
    blurb: "For high-volume creators and small presses.",
    features: [
      "Everything in Pro",
      "Highest monthly AI usage",
      "Dedicated support",
      "Team and integration path",
    ],
    highlighted: false,
    badge: null as string | null,
  },
] as const

/**
 * Sales-led enterprise offering (Epic 28 / FR361–FR380).
 * Not a self-serve $/mo plan — large houses, custom contracts.
 */
export const ENTERPRISE_PLAN = {
  id: "enterprise",
  name: "Enterprise",
  price: "Custom",
  blurb:
    "For publishing houses and large teams that need SSO, dedicated environments, and house-scale consistency.",
  features: [
    "Organization hierarchy — imprint, team, author",
    "SSO (SAML / OIDC) and role-based access",
    "Dedicated environment and SLA support",
    "Co-branded platform and custom domain",
    "Author voice fine-tuning from backlist",
    "Audit logging and data residency options",
    "Volume AI usage with enterprise billing",
  ],
  ctaLabel: "Contact sales",
  ctaHref: "/contact",
} as const

export const FAQS = [
  {
    q: "Who owns the manuscript?",
    a: "You do. Every word, every character, every world. Authorythm produces drafts and guidance; the copyright and the credit are yours.",
  },
  {
    q: "Do I need to have written a book before?",
    a: "No. The wizard was built for both career indie authors and first-timers — it asks about your story, not your résumé, and the five stages carry the structure a first book usually lacks.",
  },
  {
    q: "Which genres does it support?",
    a: "Fantasy, romance, thriller, mystery, and science fiction have the deepest trope libraries today, but the Foundation stage composes tone, setting, and heat for any commercial fiction.",
  },
  {
    q: "Can I bring my own outline?",
    a: "Yes. You can arrive with a premise, a partial outline, or a finished plan — the wizard folds what you have into the pipeline and scaffolds the rest.",
  },
  {
    q: "How does the AI actually work?",
    a: "Each stage decomposes your book into components — genre, tone, tropes, heat — and carries every choice forward, so later stages build on the earlier ones.",
  },
  {
    q: "What does it cost?",
    a: "We offer monthly plans — Hobbyist, Author, Pro, and Publisher — with included AI usage as you generate. Pricing will be listed here when plans open. Large publishing houses use Enterprise with custom pricing — contact sales.",
  },
  {
    q: "Do you work with large publishers?",
    a: "Yes. Enterprise covers organization hierarchy, SSO, dedicated environments, SLAs, co-branding, author voice fine-tuning, and compliance features. Pricing is custom — reach out via Contact.",
  },
] as const

export const NAV_LINKS = [
  { href: "/#how", label: "How it works" },
  { href: "/#features", label: "Features" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/#faq", label: "FAQ" },
] as const

/**
 * Product app origin for CTAs (sign-up, etc.).
 * Required via NEXT_PUBLIC_APP_SIGNUP_URL — no hardcoded host.
 */
export function getAppSignupUrl(): string {
  const base = process.env.NEXT_PUBLIC_APP_SIGNUP_URL?.trim()
  if (!base) {
    throw new Error(
      "Missing required env NEXT_PUBLIC_APP_SIGNUP_URL (e.g. https://alpha.authorythm.ai/auth/sign-up). No default host."
    )
  }
  return base
}

/**
 * Sign-in URL for existing authors, derived from the sign-up env's
 * origin (same app, no second env var, no hardcoded host).
 */
export function getAppSignInUrl(): string {
  return new URL("/auth/sign-in", getAppSignupUrl()).toString()
}
